import api from '../services/api'
import type { CampaignContact } from '../services/campaign-contacts'
import type { FailureCategory } from './dialFailure'

export interface TokenResponse {
  token: string
  identity: string
  ttl: number
}

export interface CreateCallResponse {
  callId: string
  status: string
  /**
   * LOT-SCÈNE-LINK : contact du tenant que le serveur a RÉSOLU pour ce numéro
   * (rattachement auto de la PR #118, match unique ou rien) ou vérifié quand
   * `contactId` était fourni. ABSENT quand rien n'est résolu — la scène garde
   * alors « Contact inconnu ».
   */
  contact?: { id: string; name: string }
}

/**
 * Cause d'échec déclinée par le serveur (issue #144, E-03) : le code brut,
 * sa catégorie (clé i18n `dialFailure.cause.<category>`) et le libellé FR de
 * l'API (informatif — la scène traduit par catégorie).
 */
export interface FailureCauseResponse {
  code: string
  message: string | null
  category: FailureCategory
  label: string
}

export interface CallSnapshot {
  id: string
  status: string
  outcome: string
  direction: string | null
  duration: number
  twilioSid: string | null
  twilioToNumber: string | null
  twilioFromNumber: string | null
  contactId: string | null
  campaignId: string | null
  startedAt: string
  twilioErrorCode?: string | null
  twilioErrorMessage?: string | null
  failure?: FailureCauseResponse | null
}

/**
 * Une ligne de l'historique d'appels de l'agent.
 *
 * Type PROPRE à la v2 plutôt que le `Call` de `services/calls.ts` : celui-ci
 * déclare un `outcome` amputé (4 valeurs sur les 8 de `schema.prisma`), et
 * c'est un fichier v1 qu'on ne modifie pas.
 */
export interface AgentCallHistoryEntry {
  id: string
  status: string
  outcome: string | null
  direction: 'INBOUND' | 'OUTBOUND' | null
  duration: number | null
  createdAt: string
  twilioToNumber: string | null
  twilioFromNumber: string | null
  contact: { id: string; name: string | null; phone: string | null } | null
}

/** Page d'historique — pagination par CURSEUR (cf. `call-history.service.ts`). */
export interface CallHistoryPage {
  entries: AgentCallHistoryEntry[]
  /** À repasser tel quel pour la page suivante. `null` = fin de liste. */
  nextCursor: string | null
}

/** Une ligne de l'historique D'UN CONTACT (endpoint v1 `/calls/contact/:id/history`). */
export interface ContactCallHistoryEntry {
  id: string
  status: string
  outcome: string | null
  direction: 'INBOUND' | 'OUTBOUND' | null
  duration: number | null
  notes: string | null
  startedAt: string
  createdAt: string
  user?: { id: string; name: string | null } | null
  campaign?: { id: string; name: string } | null
}

/** Statistiques du jour de l'agent (endpoint v1 `/calls/today-stats`). */
export interface TodayStats {
  totalCalls: number
  answeredCalls: number
  totalDuration: number
  avgDuration: number
  answerRate: number
}

/** Rappel promis dans les 24 h (endpoint v1 `/calls/callbacks/upcoming`). */
export interface UpcomingCallback {
  id: string
  scheduledCallbackTime: string | null
  contact?: { id: string; name: string | null; phone: string | null } | null
  campaign?: { id: string; name: string } | null
}

/**
 * L'APPEL À QUALIFIER DE L'AGENT (issue #220) — ce que le serveur sait encore
 * d'un wrapup que le poste a perdu en rafraîchissant : `Call` ANSWERED +
 * PENDING récent (fenêtre `AGENT_WRAPUP_EXCLUSION_SECONDS`), de CET agent.
 */
export interface PendingWrapup {
  callId: string
  direction: 'INBOUND' | 'OUTBOUND' | null
  /** Numéro du correspondant (appelant d'un entrant, destination d'un sortant). */
  phoneNumber: string | null
  contactId: string | null
  contactName: string | null
  campaignId: string | null
  campaignContactId: string | null
  /** Durée de conversation en secondes (0 si inconnue). */
  duration: number
  /** Fin de conversation (ISO). */
  endedAt: string
}

/**
 * Lecture TOLÉRANTE de `{ pending }` (REGISTRE #193 : hors contrat = rien,
 * jamais un écran cassé) : corps vide, HTML de proxy, `pending` absent ou sans
 * `callId` ⇒ `null`. Les champs facultatifs manquants sont normalisés.
 */
export function normalizePendingWrapup(data: unknown): PendingWrapup | null {
  if (!data || typeof data !== 'object') return null
  const raw = (data as { pending?: unknown }).pending
  if (!raw || typeof raw !== 'object') return null
  const p = raw as Record<string, unknown>
  if (typeof p.callId !== 'string' || !p.callId.trim()) return null
  const str = (value: unknown): string | null => (typeof value === 'string' ? value : null)
  const direction =
    p.direction === 'INBOUND' || p.direction === 'OUTBOUND' ? p.direction : null
  return {
    callId: p.callId,
    direction,
    phoneNumber: str(p.phoneNumber),
    contactId: str(p.contactId),
    contactName: str(p.contactName),
    campaignId: str(p.campaignId),
    campaignContactId: str(p.campaignContactId),
    duration: typeof p.duration === 'number' && Number.isFinite(p.duration) ? p.duration : 0,
    endedAt: str(p.endedAt) ?? new Date().toISOString(),
  }
}

/** Client REST téléphonie v2 — réutilise l'instance axios auth (mutex refresh, wake-up iPad). */
export const telephonyApi = {
  getToken: async (): Promise<TokenResponse> => {
    const { data } = await api.post('/telephony/token')
    return data
  },

  createCall: async (params: {
    toNumber: string
    contactId?: string
    campaignId?: string
    campaignContactId?: string
  }): Promise<CreateCallResponse> => {
    const { data } = await api.post('/telephony/calls', params)
    return data
  },

  setOutcome: async (
    callId: string,
    params: {
      outcome: string
      notes?: string
      scheduledCallbackTime?: string
      /**
       * LOT-SCÈNE-LINK : contact créé (ou repris) pendant la qualification —
       * le backend (`SetOutcomeDto.contactId`, PR #118) ne le rattache que si
       * le Call n'a aucun contact et qu'il appartient au tenant. La clé est
       * OMISE du corps quand absente (arité historique préservée).
       */
      contactId?: string
      /**
       * POSTE-PROMESSE (#353) : montant promis (`SetOutcomeDto.pledgedAmount`,
       * INTERESTED seul). Clé OMISE du corps quand absente.
       */
      pledgedAmount?: number
    },
  ): Promise<{ callId: string; status: string; outcome: string }> => {
    const { data } = await api.post(`/telephony/calls/${callId}/outcome`, params)
    return data
  },

  getCall: async (callId: string): Promise<CallSnapshot> => {
    const { data } = await api.get(`/telephony/calls/${callId}`)
    return data
  },

  /**
   * Annule un Call créé mais jamais établi (échec de `device.connect()`).
   *
   * Sans cet appel, le Call reste `QUEUED` sans `twilioSid` : le routage
   * entrant croit l'agent occupé et la file d'attente cesse de dépiler vers
   * lui — constat F-6 de la recette réelle. Refusé côté serveur (409) si un
   * `twilioSid` est déjà rattaché.
   */
  cancelCall: async (
    callId: string,
    /**
     * Issue #144 (E-09) : l'erreur du SDK qui a fait échouer `connect()`. Le
     * poste est la SEULE source de cause pour un appel sans SID ; le serveur la
     * persiste et rend le libellé décliné (`failure`). Sans erreur connue,
     * l'appel garde son arité historique (corps vide).
     */
    failure?: { code?: string; message?: string },
  ): Promise<{
    callId: string
    status: string
    cancelled: boolean
    failure?: FailureCauseResponse | null
  }> => {
    const body =
      failure && (failure.code || failure.message)
        ? {
            ...(failure.code ? { errorCode: failure.code } : {}),
            ...(failure.message ? { errorMessage: failure.message } : {}),
          }
        : undefined
    const { data } = body
      ? await api.post(`/telephony/calls/${callId}/cancel`, body)
      : await api.post(`/telephony/calls/${callId}/cancel`)
    return data
  },

  /**
   * PAUSE-PLATEAU (décision client 2026-10-08) — l'état de MON plateau, lu au
   * serveur (`GET /tenants/me/options`, hors zone téléphonie : tout
   * utilisateur authentifié, tenant du JWT). `canMakeCalls: false` = plateau
   * EN PAUSE : la scène affiche « Plateau en pause », pavé et porte campagne
   * désactivés. Requête d'arrière-plan : ni voile global ni toast d'erreur.
   *
   * Lecture TOLÉRANTE : hors contrat (corps vide, proxy qui rend du HTML en
   * 200), on considère le plateau ACTIF — la pause ne s'invente pas sur une
   * réponse mal formée (REGISTRE #193 : un badge qui lit `data.x.y` sans garde
   * fait tomber tout l'écran).
   */
  getTenantOptions: async (): Promise<{ canMakeCalls: boolean }> => {
    const { data } = await api.get<{ canMakeCalls?: unknown }>('/tenants/me/options', {
      headers: { 'X-Background-Request': 'true' },
    })
    return { canMakeCalls: data?.canMakeCalls !== false }
  },

  /**
   * ABANDON D'UN PRÉ-LANCEMENT (Pause, Stop, entrant pendant la fenêtre T-3 s).
   *
   * ⚠️ PAS `cancelCall` — deux différences qui comptent (retouches M-2 et M-3) :
   *  ① la RÉSERVATION du contact est CONSERVÉE : il reste présenté à l'écran et
   *    sera composé à la reprise. La libérer permettrait à un autre agent
   *    d'appeler la même personne pendant la pause — le trou v1 exact que ce
   *    verrou ferme ;
   *  ② le `Call` créé d'avance est SUPPRIMÉ, jamais clos : rien n'a été
   *    composé, et un FAILED/NO_ANSWER fantôme mentirait à l'historique du
   *    contact comme aux statistiques du jour de l'agent.
   */
  abortPrelaunch: async (callId: string): Promise<void> => {
    await api.post(`/telephony/calls/${callId}/abort-prelaunch`, undefined, {
      headers: { 'X-Background-Request': 'true' },
    })
  },

  /**
   * RAPPEL D'UN LEG EN VOL — Stop/Pause pendant la fenêtre de connexion
   * anticipée : `device.connect()` est parti, personne ne sonne encore.
   *
   * ⚠️ NI `cancelCall` NI `abortPrelaunch` ne conviennent : tous deux exigent
   * un `Call` SANS `twilioSid`, or le webhook `voice` l'a très probablement
   * déjà rattaché (l'appel est `DIALING`). `cancelCall` répondrait 409 et le
   * `Call` resterait `DIALING` pour toujours — agent réputé occupé, file
   * gelée, fiche fantôme dans l'historique du donateur.
   *
   * Comme `abortPrelaunch` : le `Call` est SUPPRIMÉ (personne n'a sonné) et la
   * RÉSERVATION du contact est CONSERVÉE (l'agent le garde sous les yeux).
   */
  abortLaunch: async (callId: string): Promise<void> => {
    await api.post(`/telephony/calls/${callId}/abort-launch`, undefined, {
      headers: { 'X-Background-Request': 'true' },
    })
  },

  /**
   * ABANDON de qualification (bouton « Ignorer » du wrapup).
   *
   * Le pendant serveur d'une action qui n'était que locale : sans lui, le
   * `Call` restait `ANSWERED`+`PENDING` en base, l'agent restait exclu du
   * routage entrant pendant toute la fenêtre de wrapup et la file d'attente
   * n'était pas relancée. Symétrique de `setOutcome` et `cancelCall`.
   */
  abandonWrapup: async (
    callId: string,
  ): Promise<{ callId: string; outcome: string; released: boolean }> => {
    const { data } = await api.post(`/telephony/calls/${callId}/abandon`)
    return data
  },

  /**
   * REPRISE APRÈS RAFRAÎCHISSEMENT (issue #220) — « ai-je un appel à
   * qualifier ? ». Le serveur répond TOUJOURS `{ pending }` (`null` sinon) ;
   * tenant et agent viennent du jeton. Requête d'arrière-plan : ni voile ni
   * toast. Une erreur réseau REMONTE : c'est le provider qui choisit de
   * l'ignorer (une reprise manquée n'est jamais pire que l'état d'avant).
   */
  getPendingWrapup: async (): Promise<PendingWrapup | null> => {
    const { data } = await api.get<unknown>('/telephony/wrapup/pending', {
      headers: { 'X-Background-Request': 'true' },
    })
    return normalizePendingWrapup(data)
  },

  /**
   * Disponibilité de l'agent — endpoint EXISTANT du CRM (`users.controller`),
   * déjà utilisé par le badge v1 : l'agent ne peut modifier QUE la sienne
   * (l'id vient du JWT côté serveur, jamais du corps de la requête).
   *
   * La v2 l'appelle pour faire suivre la disponibilité à l'état RÉEL du poste
   * (constat F-5 : des agents « joignables » en base des jours après leur
   * déconnexion faisaient sonner 25 s dans le vide).
   */
  setAvailability: async (isAvailable: boolean): Promise<void> => {
    await api.patch('/users/me/availability', { isAvailable })
  },

  /**
   * HISTORIQUE de l'agent — RECHERCHE et PAGINATION **SERVEUR**.
   *
   * Endpoint v2 (`telephony/history`) et non `GET /calls` : ce dernier pagine
   * mais ne cherche pas (aucun paramètre `search` — vérifié dans
   * `calls.service.ts`), et `src/calls/**` est protégé. La règle du cahier
   * est sans appel : jamais de « je charge tout et je filtre en JS » — c'est
   * la cause de lourdeur mesurée sur `SimpleDialerPage` (v1).
   *
   * `signal` : une frappe de recherche annule la précédente (le résultat d'une
   * requête obsolète ne doit jamais écraser celui de la frappe en cours).
   */
  listHistory: async (params: {
    search?: string
    limit?: number
    cursor?: string | null
    signal?: AbortSignal
  }): Promise<CallHistoryPage> => {
    const { data } = await api.get<CallHistoryPage>('/telephony/history', {
      params: {
        ...(params.search ? { search: params.search } : {}),
        ...(params.limit ? { limit: params.limit } : {}),
        ...(params.cursor ? { cursor: params.cursor } : {}),
      },
      signal: params.signal,
      // Écran de confort : jamais de voile de chargement global ni de toast
      // d'erreur par-dessus une scène d'appel.
      headers: { 'X-Background-Request': 'true' },
    })
    return data
  },

  /**
   * CONTACT SUIVANT **VERROUILLÉ** — préchargement de la porte campagne.
   *
   * Appelé pendant l'appel EN COURS : à la qualification, la préview s'arme
   * sans aller-retour réseau. Le contact rendu est réservé à cet agent
   * (verrou Redis renouvelé côté serveur) — deux agents de la même campagne
   * ne peuvent plus recevoir la même personne, ce que
   * `GET /campaign-contacts/:id/contacts/next` (v1) ne garantit pas.
   *
   * ⚠️ `exclude` — LA CORRECTION DU BUG DES DOUBLONS (recette du 2026-07-29).
   * Le préchargement tourne PENDANT l'appel ; l'éligibilité en base ne change
   * qu'à la qualification ; et le verrou est idempotent pour son détenteur.
   * Résultat : le serveur re-servait à l'agent LE CONTACT QU'IL AVAIT AU
   * TÉLÉPHONE, qui était donc rappelé aussitôt — et sa seconde qualification
   * écrasait la première. On transmet ici la ligne de campagne de l'appel en
   * cours ; le serveur exclut par ailleurs, de son côté, toute ligne qu'un
   * `Call` vivant concerne (les deux moitiés sont nécessaires : la nôtre
   * couvre la fenêtre où le `Call` n'a pas encore de `twilioSid`).
   */
  reserveNextContact: async (
    campaignId: string,
    options: { exclude?: string[]; signal?: AbortSignal } = {},
  ): Promise<{
    campaignContact: CampaignContact | null
    reserved: boolean
    /**
     * Appels RESTANTS dans la campagne, contact rendu inclus (SCENE-2C).
     *
     * Optionnel côté type : un backend antérieur à ce lot ne le renvoie pas, et
     * la scène doit alors afficher « — » plutôt qu'un zéro faux. C'est la
     * SOURCE DE VÉRITÉ du compteur — jamais un décompte tenu côté client, qui
     * ne connaît ni les exclusions ni le travail des autres agents.
     */
    remaining?: number
  }> => {
    const exclude = (options.exclude ?? []).filter(Boolean)
    const { data } = await api.get(
      `/telephony/campaigns/${campaignId}/next-contact`,
      {
        params: exclude.length > 0 ? { exclude: exclude.join(',') } : undefined,
        signal: options.signal,
        headers: { 'X-Background-Request': 'true' },
      },
    )
    return data
  },

  /**
   * Libère un contact préchargé qui ne sera finalement pas appelé (changement
   * de porte, arrêt de l'enchaînement, sortie de la scène). Sans elle, il
   * resterait immobilisé jusqu'à l'expiration du verrou.
   */
  /**
   * COMBIEN RESTE-T-IL À APPELER — lecture SEULE, aucune réservation.
   *
   * La carte de pré-session (porte campagne, avant lancement) a besoin du
   * volume de travail ; elle n'a aucune raison de VERROUILLER quelqu'un pour
   * l'obtenir. Passer par `reserveNextContact` gèlerait un donateur pour toute
   * l'équipe pendant que l'agent regarde son écran sans rien lancer.
   *
   * Le serveur compte avec le `where` de la sélection elle-même : les deux
   * chiffres ne peuvent pas se contredire.
   */
  getCampaignRemaining: async (campaignId: string): Promise<number | null> => {
    const { data } = await api.get<{ remaining?: number }>(
      `/telephony/campaigns/${campaignId}/remaining`,
      { headers: { 'X-Background-Request': 'true' } },
    )
    return typeof data?.remaining === 'number' ? data.remaining : null
  },

  releaseReservedContact: async (campaignContactId: string): Promise<void> => {
    await api.post(
      `/telephony/campaigns/contacts/${campaignContactId}/release`,
      undefined,
      { headers: { 'X-Background-Request': 'true' } },
    )
  },

  /**
   * Historique d'appels D'UN CONTACT — endpoint v1 EXISTANT, consommé tel
   * quel (il est déjà borné à 50 côté serveur). C'est l'onglet « Historique »
   * de la scène : ce que ce donateur a déjà répondu, sous les yeux de l'agent
   * pendant qu'il lui parle.
   */
  getContactCallHistory: async (
    contactId: string,
  ): Promise<ContactCallHistoryEntry[]> => {
    const { data } = await api.get<ContactCallHistoryEntry[]>(
      `/calls/contact/${contactId}/history`,
      { headers: { 'X-Background-Request': 'true' } },
    )
    return Array.isArray(data) ? data : []
  },

  /** Statistiques du jour de l'agent — endpoint v1 EXISTANT (bandeau de session). */
  getTodayStats: async (): Promise<TodayStats> => {
    const { data } = await api.get<TodayStats>('/calls/today-stats', {
      headers: { 'X-Background-Request': 'true' },
    })
    return data
  },

  /** Rappels promis dans les 24 h — endpoint v1 EXISTANT (chip du bandeau). */
  getUpcomingCallbacks: async (): Promise<UpcomingCallback[]> => {
    const { data } = await api.get<UpcomingCallback[]>('/calls/callbacks/upcoming', {
      headers: { 'X-Background-Request': 'true' },
    })
    return Array.isArray(data) ? data : []
  },

  /**
   * `Campaign.autoDialDelaySeconds` — le rebours de repli quand l'agent n'a
   * posé aucun réglage sur son navigateur (cf. `countdown.ts`).
   *
   * `GET /campaigns/:id` renvoie l'enregistrement complet (aucun `select`
   * restrictif côté service) : le champ y est déjà, il n'était simplement
   * jamais lu. Type local et étroit — l'interface `Campaign` de
   * `services/campaigns.ts` (v1) ne le déclare pas, et on ne la modifie pas.
   */
  getCampaignAutoDialDelay: async (campaignId: string): Promise<number | null> => {
    const { data } = await api.get<{ autoDialDelaySeconds?: number | null }>(
      `/campaigns/${campaignId}`,
      { headers: { 'X-Background-Request': 'true' } },
    )
    const delay = data?.autoDialDelaySeconds
    return typeof delay === 'number' && Number.isFinite(delay) ? delay : null
  },
}
